import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/apiClient';
import { fetchIpLocations, mergeIpLocations } from '../utils/ipLocations';
import './MemoryManagement.css';

const STATUS_LABELS = { pending: '待审核', approved: '已通过', rejected: 'AI 拒绝', hidden: '已隐藏' };

const formatIpLocation = (location, ipAddress) => {
  const value = [location?.country, location?.regionName, location?.city, location?.district]
    .filter(Boolean)
    .filter((part, index, parts) => parts.indexOf(part) === index)
    .join(' · ');
  return value || ipAddress;
};

const EditableItem = ({ item, type, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState({ nickname: item.nickname, content: item.content, status: item.status });
  const [busy, setBusy] = useState(false);

  useEffect(() => setDraft({ nickname: item.nickname, content: item.content, status: item.status }), [item]);

  const save = async (overrides = {}) => {
    const nextDraft = { ...draft, ...overrides };
    setBusy(true);
    try {
      await apiClient.put(`/memories/admin/${type}/${item.id}`, nextDraft);
      setDraft(nextDraft);
      onSaved({ ...item, ...nextDraft });
    } catch (error) {
      window.alert(error.response?.data?.message || '保存失败');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('确定永久删除这条内容吗？此操作无法撤销。')) return;
    setBusy(true);
    try {
      await apiClient.delete(`/memories/admin/${type}/${item.id}`);
      onDeleted(item.id);
    } catch (error) {
      window.alert(error.response?.data?.message || '删除失败');
      setBusy(false);
    }
  };

  return (
    <div className={`memory-admin-item status-${draft.status}`}>
      <div className="memory-admin-fields">
        <input value={draft.nickname} maxLength={20} onChange={(event) => setDraft({ ...draft, nickname: event.target.value })} aria-label="昵称" />
        <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })} aria-label="状态">
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <textarea value={draft.content} maxLength={type === 'messages' ? 500 : 300} onChange={(event) => setDraft({ ...draft, content: event.target.value })} aria-label="内容" />
      </div>
      <div className="memory-admin-meta">
        <span>#{item.id}</span>
        <span>{new Date(item.created_at).toLocaleString('zh-CN')}</span>
        <span>{item.like_count || 0} 赞</span>
        {item.ai_result && <span>AI: {item.ai_result}{item.ai_reason ? ` · ${item.ai_reason}` : ''}</span>}
        {item.submitter_ip && <span title={item.submitter_ip}>IP: {formatIpLocation(item.location, item.submitter_ip)}</span>}
      </div>
      <div className="memory-admin-actions">
        <button onClick={() => save()} disabled={busy}>保存</button>
        <button onClick={() => save({ status: draft.status === 'hidden' ? 'approved' : 'hidden' })} disabled={busy}>
          {draft.status === 'hidden' ? '设为通过' : '隐藏'}
        </button>
        <button className="danger" onClick={remove} disabled={busy}>删除</button>
      </div>
    </div>
  );
};

const MemoryManagement = ({ searchTerm = '' }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [pageInfo, setPageInfo] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [comments, setComments] = useState({});

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await apiClient.get('/memories/admin', { params: { page, limit: 20, status, q: searchTerm } });
      const nextItems = response.data.items || [];
      setItems(nextItems);
      setPageInfo({ page: response.data.page, pages: response.data.pages, total: response.data.total });
      fetchIpLocations(nextItems, 'submitter_ip')
        .then((locations) => setItems((current) => mergeIpLocations(current, locations, 'submitter_ip')))
        .catch((locationError) => console.error('Failed to load memory IP locations:', locationError));
    } catch (error) {
      console.error('Failed to load memory management:', error);
    } finally { setLoading(false); }
  }, [searchTerm, status]);

  useEffect(() => {
    const timer = setTimeout(() => load(1), 250);
    return () => clearTimeout(timer);
  }, [load]);

  const toggleComments = async (memoryId) => {
    setExpanded((previous) => ({ ...previous, [memoryId]: !previous[memoryId] }));
    if (!comments[memoryId]) {
      try {
        const response = await apiClient.get(`/memories/admin/messages/${memoryId}/comments`);
        setComments((previous) => ({ ...previous, [memoryId]: response.data }));
        fetchIpLocations(response.data, 'submitter_ip')
          .then((locations) => setComments((previous) => ({
            ...previous,
            [memoryId]: mergeIpLocations(previous[memoryId] || [], locations, 'submitter_ip')
          })))
          .catch((locationError) => console.error('Failed to load comment IP locations:', locationError));
      } catch (error) { console.error('Failed to load memory comments:', error); }
    }
  };

  return (
    <div className="memory-management">
      <div className="memory-management-toolbar">
        <div><h2>回忆留言</h2><p>共 {pageInfo.total} 条；待审核及 AI 拒绝的内容仅在此处可见。</p></div>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      {loading ? <p className="memory-admin-empty">加载中……</p> : items.length === 0 ? <p className="memory-admin-empty">没有符合条件的留言。</p> : (
        <div className="memory-admin-list">
          {items.map((item) => (
            <section className="memory-admin-card" key={item.id}>
              <header>
                <a href={`/game-details/${item.game_id}`} target="_blank" rel="noreferrer">
                  {item.titlechn || item.titleeng || `RGM-${item.game_id}`}
                </a>
                <span>{STATUS_LABELS[item.status]}</span>
              </header>
              <EditableItem
                item={item}
                type="messages"
                onSaved={(updated) => setItems((previous) => previous.map((entry) => entry.id === updated.id ? updated : entry))}
                onDeleted={(id) => setItems((previous) => previous.filter((entry) => entry.id !== id))}
              />
              <button className="memory-comments-toggle" onClick={() => toggleComments(item.id)}>
                {expanded[item.id] ? '收起评论' : `管理评论（${item.comment_count || 0}）`}
              </button>
              {expanded[item.id] && (
                <div className="memory-admin-comments">
                  {(comments[item.id] || []).length === 0 ? <p>暂无评论</p> : (comments[item.id] || []).map((comment) => (
                    <EditableItem
                      key={comment.id}
                      item={comment}
                      type="comments"
                      onSaved={(updated) => setComments((previous) => ({ ...previous, [item.id]: previous[item.id].map((entry) => entry.id === updated.id ? updated : entry) }))}
                      onDeleted={(id) => setComments((previous) => ({ ...previous, [item.id]: previous[item.id].filter((entry) => entry.id !== id) }))}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {pageInfo.pages > 1 && <div className="memory-admin-pagination">
        <button disabled={pageInfo.page <= 1 || loading} onClick={() => load(pageInfo.page - 1)}>上一页</button>
        <span>{pageInfo.page} / {pageInfo.pages}</span>
        <button disabled={pageInfo.page >= pageInfo.pages || loading} onClick={() => load(pageInfo.page + 1)}>下一页</button>
      </div>}
    </div>
  );
};

export default MemoryManagement;
